import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { FaClipboardList, FaChartLine } from "react-icons/fa";
import ProfileForm from "../components/ProfileForm";
import { QUERY_USER_ATTRIBUTE_DEFINITIONS } from "../utils/graphql/constraints/queries.js";
import { ModalForm } from "../components/Modal/ModalForm";
import { UPDATE_USER } from "../utils/graphql/users/mutations.js";
import { QUERY_MY_JOB_STATS } from "../utils/graphql/users/queries.js";
import { GET_MEETINGS_FOR_USER } from "../utils/graphql/meetings/queries";
import MatchProfileCard from "../components/MatchProfileCard";

const Profile = ({ me }) => {
  const user = me || {};
  console.log ("Rendering Profile Page with me:", user);

  const [showEditor, setShowEditor] = useState(false);
  const hasUser = Boolean(user.username);
  const { data: attrDefData } = useQuery(QUERY_USER_ATTRIBUTE_DEFINITIONS, {
    skip: !hasUser,
  });
  const { data: statsData } = useQuery(QUERY_MY_JOB_STATS, {
    skip: !hasUser,
  });
  const { data: userMeetingsData, loading: loadingUserMeetings } = useQuery(GET_MEETINGS_FOR_USER, {
    variables: { userId: user._id },
    skip: !hasUser,
  });
  const [updateUser] = useMutation(UPDATE_USER);
  const [savingTimezone, setSavingTimezone] = React.useState(false);

  const handleDetectTimezone = async () => {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!detected) return;
    setSavingTimezone(true);
    try {
      await updateUser({ variables: { _id: user._id, timezone: detected } });
    } catch (e) {
      console.error('Error saving timezone:', e);
    } finally {
      setSavingTimezone(false);
    }
  };

  const handleClearTimezone = async () => {
    setSavingTimezone(true);
    try {
      await updateUser({ variables: { _id: user._id, timezone: '' } });
    } catch (e) {
      console.error('Error clearing timezone:', e);
    } finally {
      setSavingTimezone(false);
    }
  };

  const attributeDefs = attrDefData?.userAttributeDefinitions ?? [];
  const stats = statsData?.myJobStats || {};
  const meetingAssignments = useMemo(() => {
    const meetings = userMeetingsData?.meetingsForUser ?? [];
    return meetings
      .map((meeting) => {
        let role = "alternateHost";
        if (meeting.host?._id === user._id) role = "host";
        else if (meeting.coHost?._id === user._id) role = "coHost";

        return { meeting, role };
      })
      .sort((a, b) => (a.meeting.summary || "").localeCompare(b.meeting.summary || ""));
  }, [userMeetingsData, user._id]);

  const attributeMap = useMemo(() => {
    const map = {};
    (user.attributes || []).forEach(attr => {
      map[attr.key] = attr.value;
    });
    return map;
  }, [user.attributes]);

  const formatValue = (val) => {
    if (val === null || val === undefined || val === "") return "—";
    if (Array.isArray(val)) return val.join(", ");
    if (val === true) return "Yes";
    if (val === false) return "No";
    return String(val);
  };

  const getRoleLabel = (role) => {
    if (role === "host") return "Host";
    if (role === "coHost") return "Co-Host";
    if (role === "alternateHost") return "Alternate Host";
    return role;
  };

  const dynamicRows = attributeDefs
    .filter(def => attributeMap.hasOwnProperty(def.key))
    .map(def => ({
      key: def.key,
      label: def.label || def.key,
      value: formatValue(attributeMap[def.key]),
    }));

  const staticRows = [
    { key: "username", label: "Display Name", value: user.username || "—" },
    { key: "email", label: "Email", value: user.email || "—" },
  ];
  const rows = [...staticRows, ...dynamicRows];

  if (!hasUser) {
    return (
      <h4>
        You need to be logged in to see this. Use the navigation links above to
        sign up or log in!
      </h4>
    );
  }

  return (
    <div className="container py-4" style={{ maxWidth: 900 }}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="mb-0">{me.username}</h2>
          <small className="text-muted">Profile settings</small>
        </div>
        <button className="btn btn-outline-primary" onClick={() => setShowEditor(true)}>
          Edit Profile
        </button>
      </div>

      <div className="card shadow-sm">
        <div className="list-group list-group-flush">
          {rows.map(row => (
            <div
              key={row.key}
              className="list-group-item d-flex justify-content-between align-items-center"
            >
              <div>
                <div className="fw-semibold text-dark">{row.label}</div>
                <div className="text-muted">{row.value}</div>
              </div>
            </div>
          ))}

          {/* Timezone row */}
          <div className="list-group-item d-flex justify-content-between align-items-center">
            <div>
              <div className="fw-semibold text-dark">Timezone</div>
              <div className="text-muted">
                {user.timezone || <span className="fst-italic">Not set — affects application timing score fairness</span>}
              </div>
            </div>
            <div className="d-flex gap-2 flex-shrink-0">
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={handleDetectTimezone}
                disabled={savingTimezone}
                title="Detect and save your current timezone from this browser"
              >
                {savingTimezone ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-geo-alt me-1" />Detect</>}
              </button>
              {user.timezone && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleClearTimezone}
                  disabled={savingTimezone}
                  title="Remove saved timezone"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Meeting Assignments (Read-Only) */}
      <div className="card shadow-sm mb-4 mt-4">
        <div className="card-header bg-light border-0 py-3">
          <h5 className="mb-0 d-flex align-items-center">
            <FaClipboardList className="me-2 text-primary" />
            Meeting Assignments
          </h5>
        </div>
        <div className="card-body">
          {loadingUserMeetings ? (
            <div className="text-muted small">Loading meetings...</div>
          ) : meetingAssignments.length > 0 ? (
            <ul className="list-group mb-2">
              {meetingAssignments.map(({ meeting, role }) => (
                <li key={meeting._id} className="list-group-item d-flex justify-content-between align-items-center py-1 px-2">
                  <span>
                    <span className="me-2">{meeting.summary || "(untitled)"}</span>
                    <span className="badge bg-secondary">{getRoleLabel(role)}</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted small mb-0">Not assigned to any meetings.</p>
          )}
          <small className="text-muted d-block mt-2">
            This is a read-only view of your current meeting role assignments.
          </small>
        </div>
      </div>

      {/* My Activity */}
      <div className="card shadow-sm mb-4 mt-4">
        <div className="card-header bg-light border-0 py-3">
          <h5 className="mb-0 d-flex align-items-center">
            <FaChartLine className="me-2 text-primary" />
            My Activity
          </h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-sm-6 col-lg-4 mb-3">
              <div className="card shadow-sm h-100 text-center">
                <div className="card-body">
                  <div className="stat-number text-primary fw-bold" style={{ fontSize: "2rem" }}>
                    {stats.appliedCount || 0}
                  </div>
                  <div className="stat-label text-muted">Applications Submitted</div>
                </div>
              </div>
            </div>

            <div className="col-sm-6 col-lg-4 mb-3">
              <div className="card shadow-sm h-100 text-center">
                <div className="card-body">
                  <div className="stat-number text-success fw-bold" style={{ fontSize: "2rem" }}>
                    {stats.assignedCount || 0}
                  </div>
                  <div className="stat-label text-muted">Jobs Filled</div>
                </div>
              </div>
            </div>

            <div className="col-sm-6 col-lg-4 mb-3">
              <div className="card shadow-sm h-100 text-center">
                <div className="card-body">
                  <div className="stat-number text-info fw-bold" style={{ fontSize: "2rem" }}>
                    {stats.appliedCount && stats.appliedCount > 0
                      ? `${Math.round((stats.assignedCount / stats.appliedCount) * 100)}%`
                      : "—"}
                  </div>
                  <div className="stat-label text-muted">Success Rate</div>
                </div>
              </div>
            </div>
          </div>
          <small className="text-muted d-block">
            A snapshot of your application and assignment history.
          </small>
        </div>
      </div>

      {/* Match Profile Card */}
      <MatchProfileCard
        user={user}
        attributeDefs={attributeDefs}
        stats={stats}
        onEditProfile={() => setShowEditor(true)}
      />

      {showEditor && (
        <ModalForm
          title="Edit Profile"
          onClose={() => setShowEditor(false)}
          footer={null}
        >
          <ProfileForm
            initialData={user}
            onSubmit={async (payload) => {
              try {
                await updateUser({
                  variables: {
                    _id: user._id,
                    email: payload.email,
                    attributes: payload.attributes,
                  },
                });
                setShowEditor(false);
              } catch (err) {
                console.error("Failed to update profile", err);
              }
            }}
          />
        </ModalForm>
      )}
    </div>
  );
};

export default Profile;
